'use client';

import { useTranslations } from 'next-intl';
import { Link } from '@/lib/i18n/routing';
import { useState } from 'react';
import { UserButton, useUser } from '@clerk/nextjs';

// Cross-host link target. Marketing nav lives on www.gis.gy; the dashboard
// is on app.gis.gy, so the "Dashboard" CTA is an absolute URL not an i18n Link.
const APP_URL = process.env.NEXT_PUBLIC_APP_URL || 'https://app.gis.gy';

export default function PublicNav() {
  const t = useTranslations('publicNav');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isLoaded, isSignedIn } = useUser();

  // All section links are root-anchored ("/#contact") so they work from ANY
  // page on this single-locale site (bare "#contact" only worked on the
  // homepage — dead click on brand/equipment/monitoring pages). On the
  // homepage itself, same-path hash links still behave as a plain scroll
  // (no reload).
  const navLinks = [
    { href: '/#about', label: t('about') },
    { href: '/#equipment', label: t('equipment') },
    { href: '/#partners', label: 'Brands' },
    { href: '/#services', label: t('services') },
    { href: '/monitoring', label: t('monitoring') },
    { href: '/recovery', label: 'Recovery' },
    { href: '/#contact', label: t('contact'), isButton: true },
  ];

  return (
    <nav className="navbar">
      <div className="container">
        {/* Logo */}
        <Link href="/" className="logo-text">
          GOLDMAN <span>INDUSTRIAL</span>
        </Link>

        {/* Desktop Navigation */}
        <ul className="nav-links hidden lg:flex">
          {navLinks.map((link) => {
            if (link.isButton) {
              return (
                <li key={link.href}>
                  <a href={link.href} className="nav-cta">
                    {link.label}
                  </a>
                </li>
              );
            }

            return (
              <li key={link.href}>
                <a href={link.href}>
                  {link.label}
                </a>
              </li>
            );
          })}
        </ul>

        {/* Auth (Clerk) — desktop. Defer until Clerk has hydrated to avoid
             SSR mismatch (server has no session info, client does). */}
        <div className="hidden lg:flex items-center gap-3 mr-3 min-w-[40px] min-h-[32px]">
          {isLoaded && !isSignedIn && (
            <Link
              href="/sign-in"
              className="text-sm font-medium text-gray-300 hover:text-white transition-colors"
            >
              {t('signIn')}
            </Link>
          )}
          {isLoaded && isSignedIn && (
            <>
              <a
                href={`${APP_URL}/dashboard`}
                className="text-sm font-medium text-gray-300 hover:text-white transition-colors"
              >
                {t('dashboard')}
              </a>
              <UserButton appearance={{ elements: { avatarBox: 'h-8 w-8' } }} />
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="mobile-menu-btn lg:hidden"
          aria-label="Toggle menu"
        >
          <span className={mobileMenuOpen ? 'active' : ''}></span>
          <span className={mobileMenuOpen ? 'active' : ''}></span>
          <span className={mobileMenuOpen ? 'active' : ''}></span>
        </button>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden py-4 border-t border-zinc-800 px-4">
          <ul className="flex flex-col gap-2">
            {navLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    link.isButton
                      ? 'bg-orange-500 text-white text-center'
                      : 'text-gray-300 hover:text-white hover:bg-zinc-800'
                  }`}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          {/* Mobile Auth (Clerk) — defer until Clerk hydrated. */}
          <div className="mt-4 pt-4 border-t border-zinc-800">
            {isLoaded && !isSignedIn && (
              <Link
                href="/sign-in"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
              >
                {t('signIn')}
              </Link>
            )}
            {isLoaded && isSignedIn && (
              <div className="flex items-center justify-between gap-3 px-4 py-2">
                <a
                  href={`${APP_URL}/dashboard`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm font-medium text-gray-300 hover:text-white"
                >
                  {t('dashboard')}
                </a>
                <UserButton appearance={{ elements: { avatarBox: 'h-8 w-8' } }} />
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
